import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { Waitlist } from './Waitlist.js';
import {
  FEELINGS,
  FEELINGS_SECTION,
  FOOTER,
  HERO,
  PROBLEM,
  STEPS,
  STEPS_SECTION,
  VOICE,
} from './content.js';
import { FeelingIcon, StepIcon } from './icons.js';

/**
 * One page, seven sections, no router — everything lives above the fold of a single scroll
 * and the only interactive thing on it is the waitlist form.
 *
 * The art direction follows the client's reference: near-black ground, serif display
 * headings against a sans body, and the app's own violet → pink → coral accent. The
 * reference's photography is not available to us, so where it had a lit-room photograph
 * this uses the gradient-and-glow treatment the app already ships.
 */

function Nav({ onJoin }: { onJoin: () => void }) {
  return (
    <nav className="nav">
      <a className="brand" href="#top">
        <img className="brand-mark" src="/logo-mark.png" alt="" aria-hidden="true" />
        Manasil
      </a>
      <div className="nav-links">
        <a className="on" href="#top">
          Home
        </a>
        <a href="#how">How It Works</a>
        <a href="#voice">Our Stories</a>
        <a href="#problem">About</a>
      </div>
      <button type="button" className="btn btn-primary btn-sm" onClick={onJoin}>
        {HERO.cta} <span aria-hidden="true">→</span>
      </button>
    </nav>
  );
}

function Hero({ onJoin }: { onJoin: () => void }) {
  return (
    <header className="hero" id="top">
      <div className="hero-copy">
        <p className="eyebrow">{HERO.eyebrow}</p>
        <h1 className="display">
          {HERO.titleLead} <span className="grad">{HERO.titleAccent}</span>
          <br />
          {HERO.titleTail}
        </h1>
        <div className="lede">
          {HERO.lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
        <p className="lede-strong">{HERO.closing}</p>
        <div className="hero-actions">
          <button type="button" className="btn btn-primary" onClick={onJoin}>
            <span className="btn-play" aria-hidden="true">
              ▶
            </span>
            {HERO.cta} <span aria-hidden="true">→</span>
          </button>
          <a className="btn btn-ghost" href="#voice">
            <span className="btn-play btn-play-ghost" aria-hidden="true">
              ▶
            </span>
            Hear a story
          </a>
        </div>
        <p className="scroll-cue" aria-hidden="true">
          ↓ Scroll to explore
        </p>
      </div>
      {/* The reference's own photograph, lifted from the design and kept full-bleed. */}
      <div className="hero-art">
        <img src="/hero.png" alt="A mug and headphones resting by a window at dusk" />
        <div className="hero-glow" aria-hidden="true" />
      </div>
    </header>
  );
}

function Problem() {
  return (
    <section className="section split split-problem" id="problem">
      <div>
        <p className="eyebrow">{PROBLEM.eyebrow}</p>
        <h2 className="display display-sm">
          {PROBLEM.title[0]}
          <br />
          {PROBLEM.title[1]}
          {/* The reference tints this one character, and it does a lot of work. */}
          <span className="stop">.</span>
        </h2>
      </div>
      <div className="split-body">
        <div className="problem-lines">
          {PROBLEM.lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
        <p className="problem-close">{PROBLEM.reassure}</p>
        <p className="problem-close accent">{PROBLEM.accent}</p>
      </div>
    </section>
  );
}

function Feelings({ onPick }: { onPick: (key: string) => void }) {
  return (
    <section className="section">
      <div className="panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">{FEELINGS_SECTION.eyebrow}</p>
            <h2 className="display display-sm">{FEELINGS_SECTION.title}</h2>
          </div>
          <p className="aside" aria-hidden="true">
            {FEELINGS_SECTION.aside[0]}
            <br />
            {FEELINGS_SECTION.aside[1]}
          </p>
        </div>
        {/* Each card opens the waitlist already answered with the goal it names. */}
        <div className="grid-feelings">
          {FEELINGS.map((feeling) => (
            <button
              type="button"
              className="feeling"
              key={feeling.key}
              onClick={() => onPick(feeling.key)}
              style={{ '--tint': feeling.tint } as CSSProperties}
            >
              <FeelingIcon name={feeling.key} />
              <span className="feeling-label">{feeling.label}</span>
              <span className="feeling-go" aria-hidden="true">
                →
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Steps() {
  return (
    <section className="section" id="how">
      {/* The reference keeps the label at the left margin and centres only the heading. */}
      <p className="eyebrow">{STEPS_SECTION.eyebrow}</p>
      <h2 className="display display-sm center">{STEPS_SECTION.title}</h2>
      <div className="grid-steps">
        {STEPS.map((step) => (
          <article className="step" key={step.key} style={{ '--tint': step.tint } as CSSProperties}>
            <div className="step-head">
              <span className="step-n">{step.n}</span>
              <StepIcon name={step.key} />
            </div>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/** mm:ss. The sample is under a minute, but the format should not change if it grows. */
function clock(seconds: number): string {
  const n = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
}

/**
 * A real player, not a picture of one.
 *
 * It used to be static markup with a painted-on 34% bar reading "01:42 / 05:12" — a
 * promise the page could not keep. This plays an actual story.
 *
 * Nothing is fetched until someone presses play: `preload="none"` keeps the audio off the
 * critical path of a landing page whose job is to load fast. The AAC is offered first and
 * is a quarter the size of the MP3; the MP3 is there for anything that cannot take it.
 */
function Voice() {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [length, setLength] = useState(0);
  const [broken, setBroken] = useState(false);

  // Driven by the element's own events rather than by the click, so the UI still tells the
  // truth when playback is stopped by something other than this button — another tab
  // taking the audio focus, a phone call, the end of the story.
  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    const onTime = () => setAt(el.currentTime);
    const onMeta = () => setLength(el.duration);
    const onPlay = () => setPlaying(true);
    const onStop = () => setPlaying(false);
    const onEnd = () => {
      setPlaying(false);
      setAt(0);
      el.currentTime = 0;
    };
    const onError = () => {
      setBroken(true);
      setPlaying(false);
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onStop);
    el.addEventListener('ended', onEnd);
    el.addEventListener('error', onError);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onStop);
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('error', onError);
    };
  }, []);

  function toggle() {
    const el = audio.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => setBroken(true));
    else el.pause();
  }

  function seek(event: React.MouseEvent<HTMLDivElement>) {
    const el = audio.current;
    if (!el || !Number.isFinite(el.duration)) return;
    const box = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    el.currentTime = ratio * el.duration;
    setAt(el.currentTime);
  }

  const pct = length > 0 ? Math.min(100, (at / length) * 100) : 0;

  return (
    <section className="section split voice" id="voice">
      <div>
        <h2 className="display display-sm">
          {VOICE.title[0]}
          <br />
          {VOICE.title[1]}
        </h2>
        <p className="split-body mt">{VOICE.body}</p>
      </div>
      <figure className="player">
        <img className="player-art" src="/story-thumb.png" alt="" aria-hidden="true" />
        <div className="player-body">
          <figcaption>{VOICE.attribution}</figcaption>
          <blockquote>{VOICE.quote}</blockquote>

          <audio ref={audio} preload="none">
            <source src="/sample-story.m4a" type="audio/mp4" />
            <source src="/sample-story.mp3" type="audio/mpeg" />
          </audio>

          <div className="player-bar">
            <button
              type="button"
              className="player-play"
              onClick={toggle}
              disabled={broken}
              aria-label={playing ? 'Pause the story' : 'Play a sample story'}
            >
              {playing ? '❚❚' : '▶'}
            </button>
            <div
              className="player-track"
              onClick={seek}
              role="progressbar"
              aria-label="Progress"
              aria-valuemin={0}
              aria-valuemax={Math.floor(length) || 0}
              aria-valuenow={Math.floor(at)}
            >
              <span className="player-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="player-time">
              {broken ? 'Unavailable' : `${clock(at)} / ${clock(length)}`}
            </span>
          </div>
        </div>
      </figure>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <p className="brand">
          <img className="brand-mark" src="/logo-mark.png" alt="" aria-hidden="true" />
          Manasil
        </p>
        <p className="grad tagline">{FOOTER.tagline}</p>
      </div>
      <p className="disclaimer">{FOOTER.note}</p>
      {/* Google Play requires both of these to be reachable from the web, not only from
          inside the app. They are plain static pages, so plain anchors rather than routes. */}
      <nav className="footer-links" aria-label="Legal">
        <a href="/privacy">Privacy Policy</a>
        <a href="/terms">Terms &amp; Conditions</a>
        <a href="/delete-account">Delete your account</a>
      </nav>
      <address className="footer-company">
        <strong>{FOOTER.company.name}</strong>
        <span>{FOOTER.company.address}</span>
        <span>CIN: {FOOTER.company.cin}</span>
        <a href={`mailto:${FOOTER.company.email}`}>{FOOTER.company.email}</a>
      </address>
    </footer>
  );
}

export function App() {
  const [joining, setJoining] = useState(false);
  /** Which feeling opened the dialog, if any — it preselects that goal. */
  const [picked, setPicked] = useState<string | undefined>(undefined);

  function open(key?: string) {
    setPicked(key);
    setJoining(true);
  }

  return (
    <div className="page">
      <Nav onJoin={() => open()} />
      <Hero onJoin={() => open()} />
      <Problem />
      <Feelings onPick={open} />
      <Steps />
      <Voice />
      <Footer />
      <Waitlist open={joining} onClose={() => setJoining(false)} initialPurpose={picked} />
    </div>
  );
}
